import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
  startTransition,
  ReactNode,
} from 'react';

/** User-selected theme mode */
export type ThemePreference = 'system' | 'light' | 'dark';

/** Resolved palette applied to the UI */
export type ResolvedTheme = 'light' | 'dark';

/** @deprecated Use ResolvedTheme — kept for existing imports */
export type Theme = ResolvedTheme;

export interface ThemeColors {
  background: string;
  surface: string;
  surfaceHover: string;
  headerBg: string;
  interactiveHover: string;
  text: string;
  textSecondary: string;
  textMuted: string;
  border: string;
  borderLight: string;
  divide: string;
  sidebarBackground: string;
  sidebarText: string;
  sidebarTextHover: string;
  sidebarItemHover: string;
  sidebarActive: string;
  sidebarActiveText: string;
  sidebarBorder: string;
  topbarBackground: string;
  topbarText: string;
  topbarBorder: string;
  primary: string;
  primaryHover: string;
  primaryText: string;
  primaryFg: string;
  primaryBorder: string;
  primaryBorderAll: string;
  primaryChecked: string;
  primaryCheckedHover: string;
  accent: string;
  accentHover: string;
  success: string;
  warning: string;
  error: string;
  info: string;
  /** Static Tailwind hover/focus classes (JIT-safe) */
  hoverSurface: string;
  hoverTextSecondary: string;
  hoverTextMuted: string;
  hoverText: string;
  hoverPrimaryFg: string;
  hoverBorderPrimary: string;
  hoverSidebarText: string;
  hoverSidebarActive: string;
  hoverError: string;
  hoverBorder: string;
  groupHoverText: string;
  focusRingPrimary: string;
}

const semanticColorVars: Record<
  ResolvedTheme,
  {
    bg: string;
    surface: string;
    surfaceHover: string;
    text: string;
    textSecondary: string;
    textMuted: string;
    border: string;
    primary: string;
    primaryFg: string;
    radiusCard: string;
  }
> = {
  light: {
    bg: '#f9fafb',
    surface: '#ffffff',
    surfaceHover: '#f3f4f6',
    text: '#111827',
    textSecondary: '#374151',
    textMuted: '#6b7280',
    border: '#e5e7eb',
    primary: '#4f46e5',
    primaryFg: '#4f46e5',
    radiusCard: '0.75rem',
  },
  dark: {
    bg: '#111827',
    surface: '#1f2937',
    surfaceHover: '#374151',
    text: '#f3f4f6',
    textSecondary: '#d1d5db',
    textMuted: '#9ca3af',
    border: '#374151',
    primary: '#6366f1',
    primaryFg: '#818cf8',
    radiusCard: '0.75rem',
  },
};

const themes: Record<ResolvedTheme, ThemeColors> = {
  light: {
    background: 'bg-gray-50',
    surface: 'bg-white',
    surfaceHover: 'bg-gray-100',
    headerBg: 'bg-gray-50',
    interactiveHover: 'hover:bg-gray-100',
    hoverSurface: 'hover:bg-gray-100',
    hoverTextSecondary: 'hover:text-gray-700',
    hoverTextMuted: 'hover:text-gray-500',
    hoverText: 'hover:text-gray-900',
    hoverPrimaryFg: 'hover:text-indigo-700',
    hoverBorderPrimary: 'hover:border-indigo-500',
    hoverSidebarText: 'hover:text-white',
    hoverSidebarActive: 'hover:bg-white/10',
    hoverError: 'hover:bg-red-200',
    hoverBorder: 'hover:border-gray-300',
    groupHoverText: 'group-hover:text-gray-900',
    focusRingPrimary: 'focus:ring-indigo-500',
    text: 'text-gray-900',
    textSecondary: 'text-gray-700',
    textMuted: 'text-gray-500',
    border: 'border-gray-200',
    borderLight: 'border-gray-100',
    divide: 'divide-gray-100',
    sidebarBackground: 'bg-gradient-to-b from-gray-900 to-gray-800',
    sidebarText: 'text-gray-100',
    sidebarTextHover: 'text-white',
    sidebarItemHover: 'hover:bg-white/10 hover:text-white',
    sidebarActive: 'bg-gradient-to-r from-indigo-600 to-purple-600',
    sidebarActiveText: 'text-white',
    sidebarBorder: 'border-gray-700',
    topbarBackground: 'bg-white',
    topbarText: 'text-gray-900',
    topbarBorder: 'border-gray-200',
    primary: 'bg-indigo-600',
    primaryHover: 'bg-indigo-700',
    primaryText: 'text-white',
    primaryFg: 'text-indigo-600',
    primaryBorder: 'border-l-indigo-500',
    primaryBorderAll: 'border-indigo-500',
    primaryChecked: 'peer-checked:bg-indigo-600',
    primaryCheckedHover: 'peer-checked:hover:bg-indigo-700',
    accent: 'bg-purple-600',
    accentHover: 'bg-purple-700',
    success: 'bg-green-100 text-green-800',
    warning: 'bg-yellow-100 text-yellow-800',
    error: 'bg-red-100 text-red-800',
    info: 'bg-blue-100 text-blue-800',
  },
  dark: {
    background: 'bg-gray-900',
    surface: 'bg-gray-800',
    surfaceHover: 'bg-gray-700',
    headerBg: 'bg-gray-800',
    interactiveHover: 'hover:bg-gray-700',
    hoverSurface: 'hover:bg-gray-700',
    hoverTextSecondary: 'hover:text-gray-300',
    hoverTextMuted: 'hover:text-gray-400',
    hoverText: 'hover:text-gray-100',
    hoverPrimaryFg: 'hover:text-indigo-300',
    hoverBorderPrimary: 'hover:border-indigo-500',
    hoverSidebarText: 'hover:text-white',
    hoverSidebarActive: 'hover:bg-white/10',
    hoverError: 'hover:bg-red-900/70',
    hoverBorder: 'hover:border-gray-600',
    groupHoverText: 'group-hover:text-gray-100',
    focusRingPrimary: 'focus:ring-indigo-500',
    text: 'text-gray-100',
    textSecondary: 'text-gray-300',
    textMuted: 'text-gray-400',
    border: 'border-gray-700',
    borderLight: 'border-gray-800',
    divide: 'divide-gray-700',
    sidebarBackground: 'bg-gradient-to-b from-gray-950 to-gray-900',
    sidebarText: 'text-gray-100',
    sidebarTextHover: 'text-white',
    sidebarItemHover: 'hover:bg-white/10 hover:text-white',
    sidebarActive: 'bg-gradient-to-r from-indigo-500 to-purple-500',
    sidebarActiveText: 'text-white',
    sidebarBorder: 'border-gray-800',
    topbarBackground: 'bg-gray-800',
    topbarText: 'text-gray-100',
    topbarBorder: 'border-gray-700',
    primary: 'bg-indigo-500',
    primaryHover: 'bg-indigo-600',
    primaryText: 'text-white',
    primaryFg: 'text-indigo-400',
    primaryBorder: 'border-l-indigo-500',
    primaryBorderAll: 'border-indigo-500',
    primaryChecked: 'peer-checked:bg-indigo-500',
    primaryCheckedHover: 'peer-checked:hover:bg-indigo-600',
    accent: 'bg-purple-500',
    accentHover: 'bg-purple-600',
    success: 'bg-green-900/50 text-green-200',
    warning: 'bg-yellow-900/50 text-yellow-200',
    error: 'bg-red-900/50 text-red-200',
    info: 'bg-blue-900/50 text-blue-200',
  },
};

const scrollbarVars: Record<ResolvedTheme, { track: string; thumb: string; thumbHover: string }> = {
  light: { track: '#f1f5f9', thumb: '#cbd5e1', thumbHover: '#94a3b8' },
  dark: { track: '#334155', thumb: '#475569', thumbHover: '#64748b' },
};

const AVAILABLE_THEME_PREFERENCES: ThemePreference[] = ['system', 'light', 'dark'];

function readSystemPrefersDark(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function resolveTheme(preference: ThemePreference): ResolvedTheme {
  if (preference === 'dark') return 'dark';
  if (preference === 'light') return 'light';
  return readSystemPrefersDark() ? 'dark' : 'light';
}

function normalizeThemePreference(stored: string | null): ThemePreference {
  if (stored === 'system' || stored === 'light' || stored === 'dark') return stored;
  if (stored === 'auto') return 'system';
  if (stored === 'blue' || stored === 'green' || stored === 'purple' || stored === 'macos') {
    return 'system';
  }
  return 'system';
}

interface ThemeContextType {
  /** Resolved palette (light or dark) */
  theme: ResolvedTheme;
  /** User preference including system */
  themePreference: ThemePreference;
  colors: ThemeColors;
  setTheme: (preference: ThemePreference) => void;
  availableThemes: ThemePreference[];
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [themePreference, setThemePreferenceState] = useState<ThemePreference>(() => {
    const saved = localStorage.getItem('app-theme');
    return normalizeThemePreference(saved);
  });

  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() =>
    resolveTheme(normalizeThemePreference(localStorage.getItem('app-theme'))),
  );

  const [sidebarCollapsed, setSidebarCollapsedState] = useState<boolean>(() => {
    const saved = localStorage.getItem('app-sidebar-collapsed');
    if (saved === 'true' || saved === 'false') {
      return saved === 'true';
    }
    return false;
  });

  useEffect(() => {
    setResolvedTheme(resolveTheme(themePreference));
  }, [themePreference]);

  useEffect(() => {
    if (themePreference !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      setResolvedTheme(resolveTheme('system'));
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [themePreference]);

  const setTheme = useCallback((newPreference: ThemePreference) => {
    localStorage.setItem('app-theme', newPreference);
    startTransition(() => {
      setThemePreferenceState(newPreference);
      setResolvedTheme(resolveTheme(newPreference));
    });
  }, []);

  const setSidebarCollapsed = useCallback((collapsed: boolean) => {
    localStorage.setItem('app-sidebar-collapsed', String(collapsed));
    setSidebarCollapsedState(collapsed);
  }, []);

  const colors = themes[resolvedTheme];
  const scrollbar = scrollbarVars[resolvedTheme];
  const semantic = semanticColorVars[resolvedTheme];

  const value = useMemo(
    () => ({
      theme: resolvedTheme,
      themePreference,
      colors,
      setTheme,
      availableThemes: AVAILABLE_THEME_PREFERENCES,
      sidebarCollapsed,
      setSidebarCollapsed,
    }),
    [resolvedTheme, themePreference, colors, setTheme, sidebarCollapsed, setSidebarCollapsed],
  );

  return (
    <ThemeContext.Provider value={value}>
      <div
        className={colors.background}
        data-theme={resolvedTheme}
        style={
          {
            '--scrollbar-track': scrollbar.track,
            '--scrollbar-thumb': scrollbar.thumb,
            '--scrollbar-thumb-hover': scrollbar.thumbHover,
            '--color-bg': semantic.bg,
            '--color-surface': semantic.surface,
            '--color-surface-hover': semantic.surfaceHover,
            '--color-text': semantic.text,
            '--color-text-secondary': semantic.textSecondary,
            '--color-text-muted': semantic.textMuted,
            '--color-border': semantic.border,
            '--color-primary': semantic.primary,
            '--color-primary-fg': semantic.primaryFg,
            '--radius-card': semantic.radiusCard,
          } as React.CSSProperties
        }
      >
        {children}
      </div>
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
